/**
 * The console's pages, in the order the rail lists them.
 *
 * One list, read by the rail and by the router, so a page cannot exist in the
 * navigation without a route or the other way round.
 */

import {
  BranchForkRegular,
  CommentMultipleRegular,
  DocumentTextRegular,
  HomeRegular,
  PersonQuestionMarkRegular,
  RouterRegular,
  SettingsRegular,
  TextBulletListSquareRegular,
  DataUsageRegular,
  ShieldCheckmarkRegular,
  CalendarLtrRegular,
} from '@fluentui/react-icons'
import { createElement } from 'react'
import type { ConsoleNavEntry } from './ConsoleNav'

export const NAV_ENTRIES: ConsoleNavEntry[] = [
  { value: 'overview', label: 'Overview', path: '/', icon: createElement(HomeRegular) },
  // Next to the Overview: the same figures, one row per day (DEC-28 C3).
  {
    value: 'day-by-day',
    label: 'Day by day',
    path: '/day-by-day',
    icon: createElement(CalendarLtrRegular),
  },
  {
    value: 'decisions',
    label: 'Decisions',
    path: '/decisions',
    icon: createElement(TextBulletListSquareRegular),
  },
  {
    value: 'attempts',
    label: 'Attempts',
    path: '/attempts',
    icon: createElement(PersonQuestionMarkRegular),
  },
  {
    value: 'approval-ratio',
    label: 'Approval ratio',
    path: '/approval-ratio',
    icon: createElement(DataUsageRegular),
  },
  {
    value: 'orphan-sessions',
    label: 'Orphan sessions',
    path: '/orphan-sessions',
    icon: createElement(BranchForkRegular),
  },
  {
    value: 'routing-policy',
    label: 'Routing policy',
    path: '/routing-policy',
    icon: createElement(RouterRegular),
  },
  // Beside the routing policy, because it decides before the policy does
  // (DEC-28).
  {
    value: 'wall-e-list',
    label: 'Wall-E list',
    path: '/wall-e-list',
    icon: createElement(ShieldCheckmarkRegular),
  },
  {
    value: 'communications',
    label: 'Communications',
    path: '/communications',
    icon: createElement(CommentMultipleRegular),
  },
  {
    value: 'logs',
    label: 'Logs',
    path: '/logs',
    icon: createElement(DocumentTextRegular),
  },
  {
    value: 'settings',
    label: 'Settings',
    path: '/settings',
    icon: createElement(SettingsRegular),
  },
]

/** Resolve navigation without destroying deep links such as one decision.
 *
 *  Undefined for an address that is no page. It used to answer `overview`,
 *  which put the Overview title and the Overview rail item over a page that
 *  said nothing is served here: the console contradicting itself in the one
 *  place it exists to be clear. */
export function navValueForPath(pathname: string): string | undefined {
  const first = `/${pathname.split('/')[1] ?? ''}`
  return NAV_ENTRIES.find((entry) => entry.path === first)?.value
}
